/* eslint-disable sonarjs/cognitive-complexity */
import {
  BaSeButton,
  BaSeCheckbox,
  BaSeInput,
  BaSeTheme,
  useDebounce,
} from '@base/react';
import * as Sebrae from '@sebrae/react-core';
import React from 'react';
import { useInterviewController } from '../../../../hooks/controllers/interview-controller';
import { useUfController } from '../../../../hooks/controllers/uf-controller';
import { useModule } from '../../../../hooks/module/module';
import { FilterBottom, FilterContent, FilterHeader } from './data-table-styled';

const cache = Sebrae.StorageUtil.inTemporaryStore();
const cacheKeySebrae = '@empretec:sebrae';

export const ColumnFilter = ({
  setFilter,
  setTags,
  closeFilter,
  columnId,
  searchPersonalized,
}) => {
  const {
    getFiltersByType,
    getStatusCompletingInterview,
    getStatusInterview,
    getStatusEmail,
    getFilterValues,
  } = useInterviewController();
  const { getUfs } = useUfController();
  const [listUfs, setListUfs] = React.useState([]);

  const [originalArray, setOriginalArray] = React.useState([]);
  const [columnFilter, setColumnFilter] = React.useState([]);
  const [inputValue, setInputValue] = React.useState('');
  const [showAllCities, setShowAllCities] = React.useState(false);
  const term = useDebounce(inputValue, 350);

  const { moduleSelected } = useModule();

  const handleChange = React.useCallback(
    (option) => (checked) =>
      setColumnFilter((actualFilter) => [
        ...actualFilter.filter((item) => item !== option),
        ...(checked ? [option] : []),
      ]),
    [setColumnFilter],
  );

  const getLabel = React.useCallback(
    (option, columnId, isRural = false) => {
      const labelMapper = {
        statusCompletingInterviews: getStatusCompletingInterview({
          statusCompletingInterview: option,
        }),
        statusInterviews: getStatusInterview({ statusInterview: option }),
        emails: getStatusEmail(option),
      };
      return labelMapper[columnId] ?? option;
    },
    [getStatusCompletingInterview, getStatusEmail, getStatusInterview],
  );

  const buildLabel = React.useCallback(() => {
    const columnFilterNames = columnFilter.map((item) =>
      getLabel(item, columnId),
    );

    return `${TagLabel[columnId]}${columnFilterNames
      .join(', ')
      .replace(/, ([^,]*)$/, ` e $1`)}`;
  }, [columnFilter, columnId, getLabel]);

  const buildTag = React.useCallback(
    (tags) => {
      return [
        ...tags,
        {
          label: buildLabel(),
          onClose: () => {
            setTags((actualTags) => {
              const newTags = [
                ...actualTags.filter(
                  (item) => !item.label.startsWith(TagLabel[columnId]),
                ),
              ];
              const newFilter = Object.fromEntries(
                newTags.map((tag) => {
                  const key = tag.label.split(': ')[0];
                  const items = getLabel(tag.label, key)
                    .split(': ')[1]
                    .split(/(, | e )/g)
                    .filter((f) => f !== ' e ');

                  return [[TagReverseLabel[key]], getFilterValues(items, key)];
                }),
              );

              const filter = {
                fieldOfResearch: '',
                ufs: '',
                cities: '',
                emails: '',
                statusInterviews: '',
                statusCompletingInterviews: '',
                filterCityName: '',
                ...newFilter,
              };

              setFilter(filter);
              return newTags;
            });
          },
        },
      ];
    },
    [buildLabel, columnId, getFilterValues, getLabel, setFilter, setTags],
  );

  React.useEffect(() => {
    if (!searchPersonalized) return;
    const load = async () => {
      try {
        const response = await getUfs();
        setListUfs(response);
      } catch (e) {
        setListUfs([]);
      }
    };
    load();
  }, [getUfs, searchPersonalized]);

  const returnIdBySigla = React.useCallback(
    (sigla) => {
      return listUfs.find((estado) => estado.sigla === sigla) ?? 0;
    },
    [listUfs],
  );

  const saveSelection = React.useCallback(() => {
    if (searchPersonalized) {
      const uf = returnIdBySigla(columnFilter[0]);
      if (uf !== 0) {
        setFilter((actualFilter) => ({
          ...actualFilter,
          ufId: uf.id,
        }));
      }
    } else {
      setFilter((actualFilter) => ({
        ...actualFilter,
        [columnId]: columnFilter,
      }));
    }

    setTags((tags) => {
      tags = tags.filter((item) => !item.label.startsWith(TagLabel[columnId]));
      return columnFilter.length > 0 ? buildTag(tags) : [];
    });
    return closeFilter(closeFilter);
  }, [
    buildTag,
    closeFilter,
    columnFilter,
    columnId,
    returnIdBySigla,
    searchPersonalized,
    setFilter,
    setTags,
  ]);

  const isCityColumn = React.useMemo(() => columnId === 'cities', [columnId]);

  React.useEffect(() => {
    let filtersApplied = {};
    setFilter((actualFilter) => {
      filtersApplied = { ...actualFilter };
      if (
        cache.getItem(cacheKeySebrae) !== 'NA' &&
        !filtersApplied?.fieldOfResearch
      ) {
        filtersApplied = {
          ...filtersApplied,
          ufs: [cache.getItem(cacheKeySebrae)],
        };
      }
      return actualFilter;
    });

    getFiltersByType(moduleSelected?.id, {
      filterType: filterTypeMapper[columnId],
      filterCityName: term,
      isAllCities: showAllCities,
      ...filtersApplied,
    }).then((result) => {
      setOriginalArray(result);
    });
  }, [
    columnId,
    getFiltersByType,
    moduleSelected,
    setFilter,
    showAllCities,
    term,
  ]);

  return (
    <>
      {isCityColumn && (
        <FilterHeader>
          <BaSeInput
            inputMode="search"
            value={inputValue}
            onChange={(value) => setInputValue(value)}
            iconButton={{
              typeButton: 'base-icon',
              name: 'search',
              color: BaSeTheme.colors.institucionais.cinzaSebrae60,
            }}
            placeholder="Pesquisar…"
            size="small"
            width="100%"
          />
        </FilterHeader>
      )}
      <FilterContent>
        {originalArray?.map((option, index) => (
          <Option
            key={index}
            option={option.label}
            filter={columnFilter}
            label={getLabel(option.label, columnId)}
            handleChange={handleChange}
          />
        ))}
        {isCityColumn && !showAllCities && !term && (
          <BaSeButton
            value="mostrar mais..."
            type="tertiary"
            onClick={() => setShowAllCities(true)}
          />
        )}
      </FilterContent>
      <FilterBottom>
        <BaSeButton
          value="Salvar seleção"
          onClick={saveSelection}
          color={BaSeTheme.colors.institucionais.cinzaSebrae10}
        />
      </FilterBottom>
    </>
  );
};

const Option = ({ option, filter, label, handleChange }) => {
  return (
    <BaSeCheckbox
      color={BaSeTheme.colors.institucionais.cinzaSebrae10}
      label={label}
      checked={filter.includes(option)}
      onChange={handleChange(option)}
    />
  );
};

const filterTypeMapper = {
  ufs: 'UF',
  cities: 'CITY',
  emails: 'STATUS_EMAIL',
  statusCompletingInterviews: 'STATUS_INTERVIEW',
  statusInterviews: 'STATUS_RESULT',
};

const TagLabel = {
  ufs: 'UF: ',
  cities: 'Cidade: ',
  emails: 'E-mail: ',
  statusCompletingInterviews: 'Status: ',
  statusInterviews: 'Resultado: ',
};

const TagReverseLabel = {
  UF: 'ufs',
  Cidade: 'cities',
  Email: 'emails',
  Status: 'statusCompletingInterviews',
  Resultado: 'statusInterviews',
};
